/**
 * Grade Resolver Utility
 *
 * Feature: 005-price-grade-timeline
 * Implements date-aware grade lookups for historical pricing calculations.
 */
import store from '@/store';
/** Epoch fallback date for records without effectiveDate */
const EPOCH_FALLBACK = '1970-01-01';
/**
 * Get effective date from a grade, with epoch fallback for missing dates.
 */
function getEffectiveDate(data) {
    return data.effectiveDate || EPOCH_FALLBACK;
}
/**
 * Get effective date from a grade assignment, with epoch fallback.
 */
function getAssignmentEffectiveDate(assignment) {
    return assignment.effectiveDate || EPOCH_FALLBACK;
}
/**
 * Sort grades by effectiveDate.
 * @param grades - Array of grades to sort
 * @param ascending - If true, sort oldest first; if false, sort newest first (default)
 * @returns Sorted copy of the grades array
 */
function sortGradesByEffectiveDate(grades, ascending = false) {
    return [...grades].sort((a, b) => {
        const dateA = getEffectiveDate(a.data);
        const dateB = getEffectiveDate(b.data);
        return ascending ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
}
/**
 * Sort grade assignments by effectiveDate.
 * @param assignments - Array of grade assignments to sort
 * @param ascending - If true, sort oldest first; if false, sort newest first (default)
 * @returns Sorted copy of the assignments array
 */
function sortAssignmentsByEffectiveDate(assignments, ascending = false) {
    return [...assignments].sort((a, b) => {
        const dateA = getAssignmentEffectiveDate(a);
        const dateB = getAssignmentEffectiveDate(b);
        return ascending ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
}
/**
 * Internal helper to get grade by title and date.
 * @param gradeTitle - Grade title to find
 * @param date - Target date
 * @param includeDeleted - If true, search all grades including deleted
 */
function findGradeByTitleAndDate(gradeTitle, date, includeDeleted = false) {
    const allGrades = includeDeleted
        ? store.getters['grade/all']
        : store.getters['grade/active'];
    const dateStr = date.format('YYYY-MM-DD');
    const matchingGrades = allGrades.filter((g) => g.data.title === gradeTitle);
    if (matchingGrades.length === 0) {
        throw new Error(`Platova trida "${gradeTitle}" nebyla nalezena.`);
    }
    const applicableGrades = matchingGrades.filter((g) => {
        const effectiveDate = getEffectiveDate(g.data);
        return effectiveDate <= dateStr;
    });
    if (applicableGrades.length === 0) {
        return sortGradesByEffectiveDate(matchingGrades, true)[0];
    }
    return sortGradesByEffectiveDate(applicableGrades)[0];
}
/**
 * Get the effective grade rates for a grade title on a specific date.
 *
 * @param gradeTitle - Grade title (e.g., 'L1', 'L2', 'L3')
 * @param date - Date for which to find the effective grade rates
 * @returns The effective grade record
 * @throws Error if no matching grade is found
 *
 * Example:
 *   const grade = getGradeForDate('L2', moment('2025-01-15'));
 *   console.log(grade.data.individual); // 350
 */
export function getGradeForDate(gradeTitle, date) {
    return findGradeByTitleAndDate(gradeTitle, date, false);
}
/**
 * Get the instructor's grade ID at a specific date from their grade history.
 *
 * @param instructor - The instructor object
 * @param date - Date for which to find the effective grade assignment
 * @returns Grade document ID, or null if no grade assigned
 *
 * Example:
 *   const gradeId = getInstructorGradeIdAtDate(instructor, moment('2025-01-15'));
 *   // 'grade_abc123'
 */
export function getInstructorGradeIdAtDate(instructor, date) {
    const gradeHistory = instructor.data.gradeHistory || [];
    const dateStr = date.format('YYYY-MM-DD');
    if (gradeHistory.length === 0) {
        // Fall back to current grade_id for backward compatibility
        return instructor.data.grade_id;
    }
    // Filter history entries where effectiveDate <= target date
    const applicableEntries = gradeHistory.filter((entry) => {
        const effectiveDate = getAssignmentEffectiveDate(entry);
        return effectiveDate <= dateStr;
    });
    if (applicableEntries.length === 0) {
        // No assignment before the target date
        // Return the earliest assignment (first grade the instructor had)
        const sortedEntries = sortAssignmentsByEffectiveDate(gradeHistory, true);
        return sortedEntries.length > 0 ? sortedEntries[0].gradeId : null;
    }
    // Sort by effectiveDate descending to find the most recent applicable entry
    const sortedEntries = sortAssignmentsByEffectiveDate(applicableEntries);
    return sortedEntries[0].gradeId;
}
/**
 * Get an instructor's grade record at a specific date.
 * Combines instructor grade history lookup with grade rate lookup.
 *
 * @param instructor - The instructor object
 * @param date - Date for which to find the instructor's effective grade
 * @returns The grade record the instructor held on that date
 * @throws Error if instructor has no grade assigned or grade not found
 *
 * Example:
 *   const grade = getInstructorGradeForDate(instructor, moment('2025-01-15'));
 *   console.log(grade.data.title); // 'L2'
 */
export function getInstructorGradeForDate(instructor, date) {
    const gradeId = getInstructorGradeIdAtDate(instructor, date);
    if (!gradeId) {
        throw new Error(`Instruktor "${instructor.data.name} ${instructor.data.surname}" ` +
            `nema prirazenou platovou tridu pro datum ${date.format('YYYY-MM-DD')}.`);
    }
    // Look up the grade record by ID
    const allGrades = store.getters['grade/active'];
    // Filter grades by ID
    const matchingGrades = allGrades.filter((g) => g.id === gradeId);
    if (matchingGrades.length === 0) {
        // Grade might be soft-deleted, check all grades including deleted
        const allGradesIncludingDeleted = store.getters['grade/all'];
        const deletedMatch = allGradesIncludingDeleted.find((g) => g.id === gradeId);
        if (deletedMatch) {
            // Found in deleted grades - need to get version for the date
            return getGradeVersionForDate(deletedMatch.data.title, date);
        }
        throw new Error(`Platova trida s ID "${gradeId}" nebyla nalezena pro instruktora ` +
            `"${instructor.data.name} ${instructor.data.surname}".`);
    }
    // Get the grade version effective at the target date
    const gradeTitle = matchingGrades[0].data.title;
    return getGradeForDate(gradeTitle, date);
}
/**
 * Internal helper to get grade version for a specific date.
 * Similar to getGradeForDate but uses all grades (including deleted).
 */
function getGradeVersionForDate(gradeTitle, date) {
    return findGradeByTitleAndDate(gradeTitle, date, true);
}
/**
 * Calculate payout for an instructor's work on a specific date.
 *
 * @param instructor - The instructor object
 * @param lessonType - 'individual' | 'wex' | 'kidSchool' | 'storno'
 * @param lessonLength - Number of hours
 * @param date - Date of the work (for grade rate lookup)
 * @returns Payout amount in CZK
 * @throws Error if instructor's grade cannot be determined
 *
 * Example:
 *   const payout = calculatePayoutForDate(instructor, 'individual', 2, moment('2025-01-15'));
 *   console.log(payout); // 700
 */
export function calculatePayoutForDate(instructor, lessonType, lessonLength, date) {
    const grade = getInstructorGradeForDate(instructor, date);
    // Get the rate for the lesson type
    let hourlyRate;
    switch (lessonType) {
        case 'individual':
            hourlyRate = grade.data.individual;
            break;
        case 'wex':
            hourlyRate = grade.data.wex;
            break;
        case 'kidSchool':
            hourlyRate = grade.data.kidSchool;
            break;
        case 'storno':
            hourlyRate = grade.data.storno;
            break;
        default:
            throw new Error(`Neznamy typ lekce "${lessonType}". ` +
                `Platne typy jsou: individual, wex, kidSchool, storno.`);
    }
    return hourlyRate * lessonLength;
}
